import { useEffect, useRef, useState } from 'react'
import { CheckIcon, KeyIcon, LockIcon, MailIcon, RightArrowIcon } from '../../components/Icons/iconImages'
import InputField from '../../components/InputField'
import { Controller, useForm } from 'react-hook-form';
import { validationConstants } from '../../constants/validations';
import { Button } from '../../components/Buttons';
import gsap from 'gsap';
import { useLocation } from 'react-router-dom';
import { confirmPassword, forgotPassword, verifyOTP } from '../../services/auth';
import { Input } from 'antd';

function ForgotPassword() {
  type Step = 'enterEmail' | 'resetPassword' | 'success';
  const [step, setStep] = useState<Step>('enterEmail');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const iconRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const otpInputRef = useRef<any>(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      code: '',
      password: '',
      confirmPwd: ''
    }
  });

  // Reset form when component mounts
  useEffect(() => {
    reset({
      email: '',
      code: '',
      password: '',
      confirmPwd: ''
    });
  }, []);

  // Clear password and code fields when entering reset password step and focus OTP input
  useEffect(() => {
    if (step === 'resetPassword') {
      reset({
        email: email,
        code: '',
        password: '',
        confirmPwd: ''
      });

      // Focus the OTP input after a short delay
      setTimeout(() => {
        const otpInput = document.querySelector('.ant-otp input') as HTMLInputElement;
        if (otpInput) {
          otpInput.focus();
        }
      }, 100);
    }
  }, [step]);

  const password = watch('password');
  const confirmPwd = watch('confirmPwd');
  const code = watch('code');

  // gsap fade-in
  useEffect(() => {
    const targets = [iconRef.current, headerRef.current, textRef.current, formRef.current].filter(Boolean);
    gsap.fromTo(targets, { opacity: 0 }, { opacity: 1, duration: 0.4 });
  }, [step]);

  const validLength = password?.length >= 8;
  const hasSpecialChar = /[^A-Za-z0-9]/.test(password || '');
  const passwordsMatch = password && password === confirmPwd;
  const codeValid = code?.length === 6;

  const onSubmitEmail = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      await forgotPassword(data.email);
      setEmail(data.email);
      // Clear password and code fields when moving to next step
      reset({
        email: data.email,
        code: '',
        password: '',
        confirmPwd: ''
      });
      setStep('resetPassword');
    } catch (err: any) {
      setError(err.message || 'Error sending code');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitResetPassword = async (data: any) => {
    setLoading(true);
    setError(null);
    try {
      await confirmPassword(email, data.code, data.password);
      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Invalid verification code or error resetting password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center h-screen p-[82px] gap-6">
      <div ref={iconRef}>
        {step === 'enterEmail' ? (
          <KeyIcon className="w-14 h-14 p-3 border border-gray-300 rounded-xl shadow-sm" />
        ) : step === 'success' ? (
          <div className="w-14 h-14 p-3 border border-green-300 bg-green-50 rounded-xl shadow-sm flex items-center justify-center">
            <CheckIcon className="w-8 h-8 text-green-600" />
          </div>
        ) : (
          <LockIcon className="w-14 h-14 p-3 border border-gray-300 rounded-xl shadow-sm" />
        )}
      </div>

      <div className='space-y-2'>
        <div ref={headerRef} className="text-center">
          {step === 'enterEmail' && <h2 className="text-2xl font-semibold">Forgot password?</h2>}
          {step === 'resetPassword' && <h2 className="text-2xl font-semibold">Set new password</h2>}
          {step === 'success' && <h2 className="text-2xl font-semibold">Password reset successful!</h2>}
        </div>

        <div ref={textRef} className="max-w-[360px] text-center text-sm">
          {step === 'enterEmail' && <p>No worries, we'll send you reset instructions.</p>}
          {step === 'resetPassword' && (
            <p>
              Enter the code sent to <button className='font-semibold' type="button" onClick={() => { setStep('enterEmail'); setError(null); }}>{email}</button>{" "}
              and your new password below.
            </p>
          )}
          {step === 'success' && <p>Your password has been successfully reset. You can now log in with your new password.</p>}
        </div>
      </div>

      <div ref={formRef} className="w-full max-w-[360px] space-y-4">
        {step === 'enterEmail' && (
          <form onSubmit={handleSubmit(onSubmitEmail)} className='flex flex-col gap-6'>
            <InputField
              label={'Email'}
              name="email"
              control={control}
              rules={{ required: 'Email is required' }}
              placeholder="Enter your email"
            />
            {errors.email && <p className="text-red-500">{errors.email.message}</p>}
            {error && <p className="text-red-500">{error}</p>}
            <Button type="primary" block htmlType="submit" loading={loading} disabled={loading}>
              Reset password
            </Button>
          </form>
        )}

        {step === 'resetPassword' && (
          <form onSubmit={handleSubmit(onSubmitResetPassword)} className='flex flex-col gap-5' autoComplete="off">
            <div className='pb-6'>
              <label className="block text-sm font-medium mb-2">Verification code</label>
              <Controller
                name="code"
                control={control}
                rules={{ required: 'Code is required', minLength: { value: 6, message: 'Enter 6 digits' } }}
                render={({ field }) => (
                  <Input.OTP {...field} length={6} autoFocus />
                )}
              />
              {errors.code && <p className="text-red-500 text-sm mt-1">{errors.code.message}</p>}
            </div>

            <InputField
              label={'Password'}
              type="password"
              name="password"
              control={control}
              rules={{
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                pattern: {
                  value: /^[\S]+.*[\S]+$/,
                  message: 'Password cannot start or end with spaces'
                },
                validate: (value: string) => {
                  if (!hasSpecialChar) return 'Password must contain at least one special character';
                  return true;
                }
              }}
              placeholder="New password"
              autoComplete="new-password"
            />
            <InputField
              label={'Confirm password'}
              name="confirmPwd"
              type="password"
              control={control}
              rules={{
                required: 'Confirm password is required',
                validate: (value: string) => {
                  if (value !== password) return 'Passwords do not match';
                  return true;
                }
              }}
              placeholder="Confirm new password"
              autoComplete="new-password"
            />

            <ul className="list-none space-y-2.5 text-sm">
              <li className="flex items-center gap-2">
                <CheckIcon className={`w-5 h-5 p-[5px] rounded-full text-white ${codeValid ? 'bg-green-300' : 'bg-gray-400'}`} /> Enter 6-digit code
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className={`w-5 h-5 p-[5px] rounded-full text-white ${validLength ? 'bg-green-300' : 'bg-gray-400'}`} /> Must be at least 8 characters
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className={`w-5 h-5 p-[5px] rounded-full text-white ${hasSpecialChar ? 'bg-green-300' : 'bg-gray-400'}`} /> Must contain one special character
              </li>
              <li className="flex items-center gap-2">
                <CheckIcon className={`w-5 h-5 p-[5px] rounded-full text-white ${passwordsMatch ? 'bg-green-300' : 'bg-gray-400'}`} /> Passwords match
              </li>
            </ul>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <Button
              type="primary"
              block
              htmlType="submit"
              loading={loading}
              disabled={loading || !codeValid || !validLength || !hasSpecialChar || !passwordsMatch}
            >
              Reset password
            </Button>
          </form>
        )}

        {step === 'success' && (
          <div className='flex flex-col gap-6'>
            <Button
              type="primary"
              block
              onClick={() => window.location.href = '/login'}
            >
              Continue to log in
            </Button>
          </div>
        )}
      </div>

      {step !== 'success' && (
        <Button
          type="text"
          className='text-[#535862]'
          icon={<RightArrowIcon className="w-3 h-3 rotate-180" />}
          onClick={() => {
            if (step === 'resetPassword') {
              setStep('enterEmail');
              setError(null);
              reset({
                email: '',
                code: '',
                password: '',
                confirmPwd: ''
              });
            } else {
              window.history.back();
            }
          }}
        >
          {step === 'enterEmail' ? 'Back to log in' : 'Back'}
        </Button>
      )}
    </div>
  );
}


export default ForgotPassword